/**
 * Portal search results — Step 85's requester half: where the hero search's
 * Enter and View-alls land. Two sections today — the requester's own
 * requests and the live catalogue (both scoped server-side off the session
 * principal); help articles join as a third when the KB lands (Step 33).
 * Up to 25 rows per section; q and tab live in the URL.
 */
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { BookOpen, LayoutGrid, Search, Ticket } from 'lucide-react';
import { portalApi } from '../../features/portal/api';
import { CatalogueIcon } from '../../features/fields/catalogueIcons';
import { Highlight } from '../../components/ui/highlight';
import { Input } from '../../components/ui/input';
import { Skeleton } from '../../components/ui/skeleton';
import { qk } from '../../lib/queryClient';
import { useDebouncedUrlState, useUrlState } from '../../lib/urlState';
import { cn } from '../../lib/utils';

export function PortalSearchPage() {
  const [qLive, setQ] = useDebouncedUrlState('q', '', 300);
  const [qSettled] = useUrlState('q', '');
  const [tab, setTab] = useUrlState('tab', 'all');
  const term = qSettled.trim();

  const grouped = useQuery({
    queryKey: qk.portalSearch(term),
    queryFn: () => portalApi.search(term),
    enabled: term.length >= 2,
  });
  const entityQuery = useQuery({
    queryKey: qk.portalSearchEntity(term, tab),
    queryFn: () => portalApi.search(term, tab),
    enabled: term.length >= 2 && tab !== 'all',
  });

  const counts = {
    tickets: grouped.data?.tickets?.total ?? 0,
    items: grouped.data?.items?.total ?? 0,
    articles: grouped.data?.articles?.total ?? 0,
  };
  const totalAll = counts.tickets + counts.items + counts.articles;
  const active = tab === 'all' ? grouped : entityQuery;
  const shownTickets = tab === 'all' ? grouped.data?.tickets : entityQuery.data?.tickets;
  const shownItems = tab === 'all' ? grouped.data?.items : entityQuery.data?.items;
  const shownArticles = tab === 'all' ? grouped.data?.articles : entityQuery.data?.articles;

  const TABS = [
    { key: 'all', label: 'All results', icon: Search, count: totalAll },
    { key: 'tickets', label: 'My requests', icon: Ticket, count: counts.tickets },
    { key: 'items', label: 'Services', icon: LayoutGrid, count: counts.items },
    { key: 'articles', label: 'Help articles', icon: BookOpen, count: counts.articles },
  ] as const;

  return (
    <div className="mx-auto max-w-3xl">
      <div className="relative mb-5">
        <Search className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={qLive}
          onChange={(e) => setQ(e.target.value)}
          placeholder="Search your requests, services and help…"
          aria-label="Search"
          className="h-11 pl-10 text-base"
          autoFocus
        />
      </div>

      {term.length < 2 ? (
        <p className="text-sm text-muted-foreground">Type at least two characters to search.</p>
      ) : (
        <div className="flex gap-6">
          <nav className="w-44 shrink-0 space-y-0.5">
            {TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={cn(
                  'flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-sm transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  tab === t.key
                    ? 'bg-accent font-medium text-accent-foreground'
                    : 'text-muted-foreground hover:bg-accent/60 hover:text-foreground',
                )}
              >
                <t.icon className="h-4 w-4" />
                <span className="flex-1 truncate">{t.label}</span>
                <span className="text-xs tabular-nums text-muted-foreground">{t.count}</span>
              </button>
            ))}
          </nav>

          <div className="min-w-0 flex-1 space-y-5">
            {active.isPending && (
              <div className="space-y-3">
                <Skeleton className="h-24 w-full" />
                <Skeleton className="h-24 w-full" />
              </div>
            )}
            {active.isError && (
              <div className="space-y-2 rounded-md border border-destructive/30 bg-destructive/10 p-3">
                <p className="text-sm font-medium text-destructive">Search failed</p>
                <button
                  onClick={() => active.refetch()}
                  className="text-xs text-destructive/80 underline hover:text-destructive"
                >
                  Retry
                </button>
              </div>
            )}

            {/* An entity tab with zero matches must say so — a silent blank
                pane reads as half-loaded (review finding; the agent-side page
                already did this). */}
            {!active.isPending &&
              !active.isError &&
              tab !== 'all' &&
              ((tab === 'tickets'
                ? shownTickets?.total
                : tab === 'articles'
                  ? shownArticles?.total
                  : shownItems?.total) ?? 0) === 0 && (
                <p className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
                  No matches for “{term}”.
                </p>
              )}
            {!active.isPending && !active.isError && totalAll === 0 && tab === 'all' && (
              <p className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
                No matches for “{term}”.
              </p>
            )}

            {(tab === 'all' || tab === 'tickets') && (shownTickets?.total ?? 0) > 0 && (
              <div>
                <p className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  <Ticket className="h-3.5 w-3.5" /> My requests
                </p>
                <div className="overflow-hidden rounded-md border bg-card">
                  {shownTickets!.entries.map((t) => (
                    <Link
                      key={t.id}
                      to={`/portal/requests/${t.id}`}
                      className="block border-b px-3 py-2.5 text-sm last:border-b-0 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <Highlight text={t.subject} term={term} />{' '}
                      <span className="text-muted-foreground">
                        (#{t.type === 'service_request' ? 'SR' : 'INC'}-{t.number})
                      </span>
                    </Link>
                  ))}
                  {tab === 'all' && shownTickets!.total > 3 && (
                    <button
                      className="block w-full px-3 py-2 text-left text-sm font-medium text-brand hover:bg-accent"
                      onClick={() => setTab('tickets')}
                    >
                      View all ({shownTickets!.total})
                    </button>
                  )}
                </div>
                {tab === 'tickets' && shownTickets!.total > 25 && (
                  <p className="mt-2 text-xs text-muted-foreground">
                    Showing the first 25 of {shownTickets!.total} — narrow the search, or browse{' '}
                    <Link to="/portal/requests" className="text-brand underline-offset-2 hover:underline">
                      all your requests
                    </Link>
                    .
                  </p>
                )}
              </div>
            )}

            {(tab === 'all' || tab === 'articles') && (shownArticles?.total ?? 0) > 0 && (
              <div>
                <p className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  <BookOpen className="h-3.5 w-3.5" /> Help articles
                </p>
                <div className="overflow-hidden rounded-md border bg-card">
                  {shownArticles!.entries.map((a) => (
                    <Link
                      key={a.id}
                      to={`/portal/help/${a.slug}`}
                      className="flex items-center gap-2.5 border-b px-3 py-2.5 text-sm last:border-b-0 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <BookOpen className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="min-w-0">
                        <span className="block truncate">
                          <Highlight text={a.title} term={term} />
                        </span>
                        <span className="block truncate text-xs text-muted-foreground">{a.folderName}</span>
                      </span>
                    </Link>
                  ))}
                  {tab === 'all' && shownArticles!.total > 3 && (
                    <button
                      className="block w-full px-3 py-2 text-left text-sm font-medium text-brand hover:bg-accent"
                      onClick={() => setTab('articles')}
                    >
                      View all ({shownArticles!.total})
                    </button>
                  )}
                </div>
              </div>
            )}

            {(tab === 'all' || tab === 'items') && (shownItems?.total ?? 0) > 0 && (
              <div>
                <p className="mb-1.5 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                  <LayoutGrid className="h-3.5 w-3.5" /> Services
                </p>
                <div className="overflow-hidden rounded-md border bg-card">
                  {shownItems!.entries.map((it) => (
                    <Link
                      key={it.id}
                      to={`/portal/catalogue/${it.id}`}
                      className="flex items-center gap-2.5 border-b px-3 py-2.5 text-sm last:border-b-0 hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-brand/10 text-brand">
                        <CatalogueIcon name={it.icon} className="h-4 w-4" />
                      </span>
                      <span className="min-w-0">
                        <span className="block truncate">
                          <Highlight text={it.name} term={term} />
                        </span>
                        {it.description && (
                          <span className="block truncate text-xs text-muted-foreground">
                            {it.description}
                          </span>
                        )}
                      </span>
                    </Link>
                  ))}
                  {tab === 'all' && shownItems!.total > 3 && (
                    <button
                      className="block w-full px-3 py-2 text-left text-sm font-medium text-brand hover:bg-accent"
                      onClick={() => setTab('items')}
                    >
                      View all ({shownItems!.total})
                    </button>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
